/**
 * OaSIS Explorer Frontend - Classification Detail Modal Component
 * Task 25.12: Redesigned with Option C - Side-by-side tree + detail view
 * Task 32.6: Version-aware API calls
 * 
 * Features:
 * - Tree navigation panel on the left
 * - Detail view on the right
 * - Inline navigation without closing modal
 * - Occupation tags (OccupationCodePrefix, BroadCategoryTag, TeerTag)
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Layers, 
  FolderOpen, 
  FileText, 
  User,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { useLanguage } from '../../contexts';
import { useDatasetVersion } from '../../hooks';
import { trpc } from '../../api';
import { 
  QueryLoadingState, 
  OccupationCodePrefix,
  BroadCategoryTag,
  TeerTag,
  OasisCodeTag
} from '../common';
import { getTeerDescriptionFull, getBroadCategoryDescription } from '../../utils/occupationConstants';

interface ClassificationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  itemType: 'occupation' | 'taxonomy';
  itemLevel?: number;
  onNavigate?: (itemId: string, type: 'occupation' | 'taxonomy', level?: number) => void;
}

// Structure types for taxonomy levels
type StructureType = 'Broad Category' | 'TEER' | 'Major Group' | 'Sub-major Group' | 'Minor Group' | 'NOC Unit Group' | 'Occupation';

// Hierarchy node interface
interface HierarchyNode {
  code: string;
  title: string;
  level: number;
  occupationCount: number;
  nocDescription?: string | null;
  children?: HierarchyNode[];
}

// Breadcrumb interface
interface BreadcrumbItem {
  code: string;
  title: string;
  level: number;
}

// Occupation data interface
interface OccupationData {
  oasisCode: string;
  title: string;
  oasisLabel?: string; // Description/overview of the occupation
  leadStatement?: string;
  employmentRequirements?: string;
  mainDuties?: string[];
  exampleTitles?: string[];
  workplaceEmployers?: string[];
  additionalInfo?: string;
  hollandCode1?: string;
  hollandCode2?: string;
  hollandCode3?: string;
}

// Holland Code info interface
interface HollandCodeInfo {
  code: string;
  name: string;
  position: number;
}

const getLevelStructureType = (level: number): StructureType => {
  switch (level) {
    case 1: return 'Broad Category';
    case 2: return 'TEER';
    case 3: return 'Major Group';
    case 4: return 'Sub-major Group';
    case 5: return 'Minor Group';
    case 6: return 'NOC Unit Group';
    case 7: return 'Occupation';
    default: return 'Occupation';
  }
};

const getTypeIcon = (structureType: StructureType) => {
  switch (structureType) {
    case 'Broad Category': return Layers;
    case 'TEER': return FolderOpen;
    case 'Major Group': return FolderOpen;
    case 'Sub-major Group': return FolderOpen;
    case 'Minor Group': return FolderOpen;
    case 'NOC Unit Group': return FileText;
    case 'Occupation': return User;
    default: return FileText;
  }
};



// Measurement field interface for Holland Codes
interface MeasurementField {
  field: string;
  label: string;
  guideCode?: string;
}

interface MeasurementFieldsData {
  categories?: {
    interests?: {
      fields?: MeasurementField[];
    };
  };
}

// Extract Holland Codes from occupation data and match with measurement fields
const extractHollandCodes = (
  occupationData: OccupationData | undefined | null,
  measurementFieldsData: MeasurementFieldsData | undefined | null
): HollandCodeInfo[] | undefined => {
  if (!measurementFieldsData?.categories?.interests?.fields || !occupationData) {
    return undefined;
  }

  const hollandCodeFields = measurementFieldsData.categories.interests.fields.filter(
    (field: MeasurementField) => field.field.startsWith('holland_') && field.guideCode
  );

  const hollandCodesArray: HollandCodeInfo[] = [];
  
  // Get hollandCode1, hollandCode2, hollandCode3 from occupationData
  for (let position = 1; position <= 3; position++) {
    const fieldName = `hollandCode${position}` as keyof OccupationData;
    const code = occupationData[fieldName] as string | undefined;
    
    if (code) {
      // Find matching field in measurementFields by guideCode
      const normalizedCode = String(code).toUpperCase();
      const hollandField = hollandCodeFields.find(
        (field: MeasurementField) => {
          // Extract Holland letter from guideCode (e.g., "C01a01" -> "C")
          // or match by field name pattern holland_X where X is the letter
          const fieldLetter = field.field.replace('holland_', '').toUpperCase();
          return fieldLetter === normalizedCode;
        }
      );

      if (hollandField) {
        hollandCodesArray.push({
          code: normalizedCode,
          name: hollandField.label,
          position,
        });
      }
    }
  }

  return hollandCodesArray.length > 0 ? hollandCodesArray : undefined;
};

/**
 * Classification Detail Modal Component
 * 
 * Redesigned with Option C: Side-by-side tree + detail view
 * Enables seamless hierarchy navigation without closing the modal
 */
export const ClassificationDetailModal: React.FC<ClassificationDetailModalProps> = React.memo(({ 
  isOpen, 
  onClose, 
  itemId: initialItemId, 
  itemType: initialItemType,
  itemLevel: initialItemLevel,
  onNavigate
}) => {
  const { language } = useLanguage();
  const { version } = useDatasetVersion(); // Task 32.6: Get version from URL
  
  // Internal navigation state - allows browsing without closing modal
  // We track level because some levels (5 and 6) can have the same code
  const [currentItemId, setCurrentItemId] = useState(initialItemId);
  const [currentItemType, setCurrentItemType] = useState(initialItemType);
  const [currentLevel, setCurrentLevel] = useState<number | null>(null);
  
  // Resizable nav pane state
  const [navWidth, setNavWidth] = useState(288); // 288px = w-72 default
  const [isResizing, setIsResizing] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const MIN_NAV_WIDTH = 200;
  const MAX_NAV_WIDTH = 500;

  // Reset internal state when props change (modal reopened with new item)
  useEffect(() => {
    setCurrentItemId(initialItemId);
    setCurrentItemType(initialItemType);
    setCurrentLevel(initialItemLevel ?? null); // Will be determined from data when absent
  }, [initialItemId, initialItemType, initialItemLevel]);

  // Fetch occupation details if itemType is 'occupation'
  // Task 32.6: Include version parameter
  const occupationQuery = trpc.occupation.getByCode.useQuery(
    { oasisCode: currentItemId, language, version },
    { 
      enabled: isOpen && currentItemType === 'occupation',
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch hierarchy data for tree navigation
  // Task 32.6: Include version parameter
  const hierarchyQuery = trpc.classification.getHierarchy.useQuery(
    { language, version, maxDepth: 7 },
    { 
      enabled: isOpen,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch measurement fields to get Holland Code labels for interests
  // Task 32.6: Include version parameter
  const measurementFieldsQuery = trpc.occupation.getMeasurementFields.useQuery(
    { language, version },
    {
      enabled: isOpen && currentItemType === 'occupation',
      staleTime: 10 * 60 * 1000, // 10 minutes - measurement fields don't change often
    }
  );

  // Find taxonomy node in hierarchy by code and optionally by level
  // Level is needed because level 5 and 6 can have the same code
  const findTaxonomyNode = useCallback((nodes: HierarchyNode[], code: string, level?: number | null): HierarchyNode | null => {
    for (const node of nodes) {
      // If level is specified, match both code and level
      // If no level specified, match code only (returns first match)
      if (node.code === code && (level === undefined || level === null || node.level === level)) {
        return node;
      }
      if (node.children) {
        const found = findTaxonomyNode(node.children, code, level);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Build breadcrumb path to current node
  // targetLevel is needed because some levels (5 and 6) can have the same code
  const buildBreadcrumbs = useCallback((
    nodes: HierarchyNode[], 
    targetCode: string, 
    targetLevel?: number | null,
    path: BreadcrumbItem[] = []
  ): BreadcrumbItem[] | null => {
    for (const node of nodes) {
      const newPath = [...path, { code: node.code, title: node.title, level: node.level }];
      // Match by code and optionally by level
      if (node.code === targetCode && (targetLevel === undefined || targetLevel === null || node.level === targetLevel)) {
        return newPath;
      }
      if (node.children) {
        const found = buildBreadcrumbs(node.children, targetCode, targetLevel, newPath);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const taxonomyNode = useMemo((): HierarchyNode | null => {
    if (!hierarchyQuery.data) return null;
    const data = hierarchyQuery.data as HierarchyNode[];
    if (currentItemType === 'taxonomy') {
      // Use level if available to distinguish between nodes with same code
      return findTaxonomyNode(data, currentItemId, currentLevel);
    }
    // For occupations, we don't need a taxonomy node - the occupation is the leaf
    // We use API breadcrumbs for the path instead
    return null;
  }, [currentItemType, hierarchyQuery.data, currentItemId, currentLevel, findTaxonomyNode]);

  // Sync currentLevel with taxonomyNode level if currentLevel is null
  useEffect(() => {
    if (currentItemType === 'taxonomy' && taxonomyNode && currentLevel === null) {
      setCurrentLevel(taxonomyNode.level);
    }
  }, [taxonomyNode, currentLevel, currentItemType]);

  const treeBreadcrumbs = useMemo((): BreadcrumbItem[] => {
    // Build from hierarchy using level to distinguish same-code nodes
    if (!hierarchyQuery.data) return [];
    const data = hierarchyQuery.data as HierarchyNode[];
    return buildBreadcrumbs(data, currentItemId, currentLevel) || [];
  }, [hierarchyQuery.data, currentItemId, currentLevel, buildBreadcrumbs]);

  // Handle navigation to different items
  // Level is needed to distinguish between nodes with the same code (e.g., level 5 and 6)
  const handleNavigate = useCallback((code: string, type: 'occupation' | 'taxonomy', level?: number) => {
    setCurrentItemId(code);
    setCurrentItemType(type);
    setCurrentLevel(level ?? null);
    onNavigate?.(code, type, level);
  }, [onNavigate]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle nav pane resize
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!navRef.current) return;
      const modalRect = navRef.current.parentElement?.getBoundingClientRect();
      if (!modalRect) return;
      
      const newWidth = e.clientX - modalRect.left;
      setNavWidth(Math.min(MAX_NAV_WIDTH, Math.max(MIN_NAV_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isOpen) return null;

  const isLoading = (currentItemType === 'occupation' && occupationQuery.isLoading) || hierarchyQuery.isLoading;
  const hasError = (currentItemType === 'occupation' && occupationQuery.isError) || hierarchyQuery.isError;
  
  const structureType = currentItemType === 'occupation' 
    ? 'Occupation' as StructureType
    : taxonomyNode 
      ? getLevelStructureType(taxonomyNode.level) 
      : 'Occupation' as StructureType;

  const TypeIcon = getTypeIcon(structureType);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        // Close modal if clicking on backdrop (not on modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 w-full md:w-[1200px] h-[85vh] overflow-hidden flex shadow-2xl mx-4 ${isResizing ? 'select-none' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Left: Tree navigation */}
        <div 
          ref={navRef}
          style={{ width: navWidth }}
          className="hidden md:flex border-r border-gray-200 dark:border-slate-700 flex-col bg-gray-50 dark:bg-slate-950 relative shrink-0"
        >
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <span className="text-gray-600 dark:text-slate-400 text-xs uppercase tracking-wider font-medium">Navigation</span>
          </div>
          
          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            className={`absolute top-0 right-0 w-1 h-full cursor-col-resize group hover:bg-blue-500/50 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-300 dark:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <QueryLoadingState query={hierarchyQuery} loadingText="Loading..." wrapper="none">
              <div className="space-y-0.5">
                {/* Breadcrumb path as tree items */}
                {treeBreadcrumbs.map((crumb, i) => {
                  const Icon = getTypeIcon(getLevelStructureType(crumb.level));
                  // Check both code AND level for active state (level 5 and 6 can have same code)
                  // Must compare level to avoid highlighting both level 5 and 6 
                  const isActive = currentItemType === 'taxonomy' && 
                    crumb.code === currentItemId && 
                    crumb.level === currentLevel;
                  return (
                    <button
                      key={`${crumb.code}-${crumb.level}`}
                      onClick={() => handleNavigate(crumb.code, 'taxonomy', crumb.level)}
                      style={{ paddingLeft: `${i * 12 + 8}px` }}
                      className={`w-full flex items-center gap-2 py-2.5 pr-3 rounded-lg text-left text-sm transition-colors ${
                        isActive 
                          ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      title={crumb.title}
                    >
                      <Icon className="w-4 h-4 shrink-0 text-gray-600 dark:text-slate-300" />
                      <span className="truncate">{crumb.title}</span>
                    </button>
                  );
                })}
                
                {/* Children of current taxonomy node */}
                {taxonomyNode?.children && taxonomyNode.children.length > 0 && (
                  <>
                    {taxonomyNode.children.map((child: HierarchyNode) => {
                      const Icon = getTypeIcon(getLevelStructureType(child.level));
                      // Level 7 is occupation, others are taxonomy
                      const childType = child.level === 7 ? 'occupation' : 'taxonomy';
                      // Check both code AND level for active state
                      // Must compare level to avoid highlighting both level 5 and 6 (same code)
                      const isChildActive = currentItemType === childType && 
                        child.code === currentItemId && 
                        child.level === currentLevel;
                      return (
                        <button
                          key={`${child.code}-${child.level}`}
                          onClick={() => handleNavigate(child.code, childType, child.level)}
                          style={{ paddingLeft: `${treeBreadcrumbs.length * 12 + 8}px` }}
                          className={`w-full flex items-center gap-2 py-2.5 pr-3 rounded-lg text-left text-sm transition-colors ${
                            isChildActive
                              ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                              : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                          }`}
                          title={child.title}
                        >
                          <Icon className="w-4 h-4 shrink-0 text-gray-600 dark:text-slate-300" />
                          <span className="truncate">{child.title}</span>
                          {child.occupationCount > 0 && (
                            <span className="ml-auto text-xs text-gray-500 dark:text-slate-500">{child.occupationCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </>
                )}

                {/* Show occupation in tree if viewing occupation */}
                {currentItemType === 'occupation' && occupationQuery.data && (
                  <button
                    style={{ paddingLeft: `${treeBreadcrumbs.length * 12 + 8}px` }}
                    className="w-full flex items-center gap-2 py-2.5 pr-3 rounded-lg text-left text-sm bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400"
                  >
                    <User className="w-4 h-4 shrink-0" />
                    <span className="truncate">{(occupationQuery.data as OccupationData).title}</span>
                  </button>
                )}
              </div>
            </QueryLoadingState>
          </div>
        </div>
        
        {/* Right: Detail view */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between gap-2">
            <div className="flex h-8 items-stretch gap-2.5 min-w-0">
              <div className="w-8 h-full rounded-md flex items-center justify-center bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
                <TypeIcon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 flex h-full items-stretch overflow-hidden">
                <h2 className="flex h-full m-0 items-center text-gray-900 dark:text-white font-semibold text-sm leading-none truncate">
                  {currentItemType === 'occupation' 
                    ? (occupationQuery.data as OccupationData | undefined)?.title || 'Loading...'
                    : taxonomyNode?.title || 'Loading...'}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {currentItemType === 'occupation' && currentItemId && (
                <a
                  href={`/${version}/occupation/${currentItemId}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 text-xs rounded-md font-medium transition-colors hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  View full occupation details
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button 
                onClick={onClose}
                className="p-1 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              {currentItemType === 'taxonomy' && taxonomyNode && (
                <>
                  <OccupationCodePrefix prefix={taxonomyNode.code} level={taxonomyNode.level} />
                  <span className="text-sm text-gray-600 dark:text-slate-500">{structureType}</span>
                </>
              )}
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 dark:border-slate-600 border-t-blue-500 rounded-full" />
              </div>
            ) : hasError ? (
              <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400">Failed to load details</p>
              </div>
            ) : currentItemType === 'occupation' && occupationQuery.data ? (
              <OccupationDetailContent 
                occupation={occupationQuery.data as OccupationData} 
                hollandCodes={extractHollandCodes(occupationQuery.data as OccupationData, measurementFieldsQuery.data)}
                version={version}
              />
            ) : taxonomyNode ? (
              <TaxonomyDetailContent 
                node={taxonomyNode}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-slate-500 italic">No details available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

// Occupation detail content component
interface OccupationDetailContentProps {
  occupation: OccupationData;
  hollandCodes?: HollandCodeInfo[];
  version: string;
}

const OccupationDetailContent: React.FC<OccupationDetailContentProps> = ({ 
  occupation, 
  hollandCodes,
  version
}) => {
  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/50 p-4">
        <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mb-2">
          OaSIS {occupation.oasisCode}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BroadCategoryTag oasisCode={occupation.oasisCode} size="sm" />
          <TeerTag oasisCode={occupation.oasisCode} size="sm" />
          {hollandCodes?.map((holland, index) => (
            <span
              key={`${holland.code}-${index}`}
              className="inline-flex items-center px-2.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md text-xs text-gray-600 dark:text-slate-300"
            >
              {index + 1}. {holland.name} ({holland.code})
            </span>
          ))}
        </div>
        {occupation.leadStatement && (
          <p className="mt-4 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
            {occupation.leadStatement}
          </p>
        )}
      </section>

      {occupation.oasisLabel && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
          <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">{occupation.oasisLabel}</p>
        </section>
      )}

      {occupation.employmentRequirements && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Employment Requirements</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
            {occupation.employmentRequirements
              .split('\n')
              .filter(line => line.trim())
              .map((line, index) => (
                <li key={index}>{line.trim()}</li>
              ))}
          </ul>
        </section>
      )}

      {occupation.mainDuties && occupation.mainDuties.length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
            Main Duties
            <span className="text-sm font-normal text-gray-500 dark:text-slate-400 ml-1">({occupation.mainDuties.length})</span>
          </h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-700 dark:text-slate-300">
            {occupation.mainDuties.map((duty: string, index: number) => (
              <li key={index} className="leading-relaxed">{duty}</li>
            ))}
          </ol>
        </section>
      )}

      {((occupation.exampleTitles && occupation.exampleTitles.length > 0) ||
        (occupation.workplaceEmployers && occupation.workplaceEmployers.length > 0)) && (
        <section className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="space-y-5">
            {occupation.workplaceEmployers && occupation.workplaceEmployers.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Typical Workplaces
                  <span className="text-sm font-normal text-gray-500 dark:text-slate-400 ml-1">({occupation.workplaceEmployers.length})</span>
                </h3>
                <ul className="list-disc list-inside space-y-0.5 text-sm text-gray-700 dark:text-slate-300">
                  {occupation.workplaceEmployers.map((employer: string, index: number) => (
                    <li key={index}>{employer}</li>
                  ))}
                </ul>
              </div>
            )}
            {occupation.exampleTitles && occupation.exampleTitles.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Example Titles
                  <span className="text-sm font-normal text-gray-500 dark:text-slate-400 ml-1">({occupation.exampleTitles.length})</span>
                </h3>
                <ul className="list-disc list-inside space-y-0.5 text-sm text-gray-700 dark:text-slate-300">
                  {occupation.exampleTitles.map((title: string, index: number) => (
                    <li key={index}>{title}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {occupation.additionalInfo && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Additional Information</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
            {(
              occupation.additionalInfo.includes('\n')
                ? occupation.additionalInfo.split('\n')
                : occupation.additionalInfo.match(/[^.!?]+[.!?]+/g) || [occupation.additionalInfo]
            )
              .map((item) => item.trim())
              .filter(Boolean)
              .map((item, index) => (
                <li key={index}>{item}</li>
              ))}
          </ul>
        </section>
      )}

      <section className="pt-4 border-t border-gray-200 dark:border-slate-700">
        <a
          href={`/${version}/occupation/${occupation.oasisCode}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 text-xs rounded-md font-medium transition-colors hover:bg-gray-50 dark:hover:bg-slate-700"
        >
          View full occupation details
          <ExternalLink className="w-3 h-3" />
        </a>
      </section>
    </div>
  );
};

// Taxonomy detail content component
interface TaxonomyDetailContentProps {
  node: HierarchyNode;
}

const TaxonomyDetailContent: React.FC<TaxonomyDetailContentProps> = ({ node }) => {
  // Get hardcoded descriptions for Broad Categories and TEER levels from shared constants
  // Level 1 = Broad Category, Level 2 = TEER
  const broadCategoryDesc = node.level === 1 ? getBroadCategoryDescription(node.code) : null;
  // For TEER level, extract second digit from code (e.g., "00" -> "0", "12" -> "2")
  const teerLevelCode = node.level === 2 && node.code.length >= 2 ? node.code[1] : null;
  const teerDesc = teerLevelCode !== null ? getTeerDescriptionFull(teerLevelCode) : null;
  const hasHardcodedDescription = broadCategoryDesc || teerDesc;
  
  return (
              <div className="space-y-6">
      {/* Description */}
      {hasHardcodedDescription ? (
        <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
          {broadCategoryDesc || teerDesc}
        </p>
      ) : node.nocDescription ? (
        <p className="text-gray-700 dark:text-slate-300 leading-relaxed">{node.nocDescription}</p>
      ) : (
        <p className="text-gray-500 dark:text-slate-500 italic">No description available for this category.</p>
      )}

    </div>
  );
};
